<script lang="ts" setup>
import { useTranslations } from '~/composables/translations';
import vuIcon from '@ds/vuekit/src/components/vu-icon.vue';
import { computed } from 'vue';
import { requirejs } from '~/modules/require';

const { $i18n } = useTranslations('components/homepage/bottom_links');

type HomepageBottomsLinksOptions = {
  isSeller: boolean;
};

const props = withDefaults(defineProps<HomepageBottomsLinksOptions>(), {
  isSeller: false,
});

const links = computed(() =>
  [
    { name: $i18n('exploreLabs'), to: 'LabsExploration' },
    { name: $i18n('printNow'), to: 'QuoteUploadPart' },
    { name: $i18n('myPrints'), to: 'MyPrints' },
    props.isSeller ? { name: '', to: '' } : { name: $i18n('becomePartner'), to: 'BecomeAPartner' },
    { name: $i18n('faq'), to: 'FAQ' },
    { name: $i18n('wikiProcesses'), to: 'WikiProcesses' },
    { name: $i18n('wikiMaterials'), to: 'WikiMaterials' },
    { name: $i18n('about3DS'), to: '3ds' },
    {
      name: $i18n('cookies'),
      async callback() {
        const [GDPR] = await requirejs<[any]>(['DS/GDPR/GDPR']);
        GDPR.start();
      },
    },
  ].filter((el) => el.name),
);
const bolder3D = (value: string) => value.replaceAll('3DEXPERIENCE', '<b>3D</b>EXPERIENCE');
</script>
<template>
  <div class="homepage-bottom-links-container px-30px">
    <div
      class="homepage-max-content-container flex flex-column--sm justify-space-between align-items-center--sm grid-gap-20px py-20px"
    >
      <div class="flex flex-column grid-gap-5px text-center--sm links">
        <div class="footer-title">
          {{ $i18n('links') }}
        </div>
        <div class="flex flex-column">
          <template v-for="link in links" :key="link.name">
            <router-link
              :to="{ name: link.to }"
              :id="`footer-${link.name.replaceAll(' ', '')}`"
              v-if="!link.callback"
            >
              {{ link.name }}
            </router-link>
            <a
              v-else
              :id="`footer-${link.name.replaceAll(' ', '')}`"
              @click="link.callback()"
              class="pointer"
            >
              {{ link.name }}
            </a>
          </template>
        </div>
      </div>
      <div class="flex bottom-links-center-container grid-gap-10px text-center--sm">
        <img
          src="/assets/images/homepage/footer.png"
          loading="lazy"
          class="hide--sm"
          alt="3DPrinting machine"
        />
        <div class="flex flex-column grid-gap-5px">
          <div class="footer-title" v-html="bolder3D($i18n('title'))" />
          <div class="footer-content" v-html="bolder3D($i18n('content'))" />
        </div>
      </div>
      <div class="flex flex-column grid-gap-5px text-center--sm">
        <div class="footer-title">
          {{ $i18n('contact') }}
        </div>
        <div>
          <a
            class="flex flex-row justify-center--sm grid-gap-5px"
            href="https://www.3ds.com/make/3dexperience-make-contact-us"
            target="_blank"
          >
            <vu-icon color="white--text" icon="mail" />
            {{ $i18n('contactUs') }}
          </a>
          <a
            class="flex flex-row justify-center--sm grid-gap-5px"
            href="https://www.3ds.com/support/contact-your-support-center"
            target="_blank"
          >
            <vu-icon color="white--text" icon="i-question" />
            {{ $i18n('findLocalSupport') }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
@use '/assets/scss/variables' as variables;

.homepage-bottom-links-container {
  background-color: #003c5a;
  color: #65899b;
}

img {
  width: 100px;
  height: 135px;
}

.footer-title {
  line-height: 22px;
  color: white;
  font-size: 18px;

  @include variables.for-sm {
    line-height: 20px;
    font-size: 16px;
  }
}

.bottom-links-center-container {
  max-width: 50%;

  @include variables.for-sm {
    max-width: unset;
  }
}

a {
  color: white;
  opacity: 0.8;
}

.footer-content {
  color: white;
  opacity: 0.8;
}
</style>
